import { Routes } from '@angular/router';
import { authGuard, guestGuard, loginGuard, otpVerificationGuard } from './core/guards/auth.guard';
import { clientIdGuard } from './core/guards/client-id.guard';
import { userResolver } from './core/resolvers/user.resolver';
import { invitationTokenResolver } from './core/resolvers/invitation-token.resolver';
import { passwordResetTokenResolver } from './core/resolvers/password-reset-token.resolver';
import { permissionGuard } from './core/guards/permission.guard';
import { partialViewActiveGuard } from './core/guards/partial-view-active.guard';
import { apiUserResolver } from './core/resolvers/api-user.resolver';
import { FeatureName } from '../@backoffice/services/permission.service';
import { unsavedChangesGuard } from './pages/campaigns/campaign-wizard/unsaved-changes.guard';
import { newCampaignRequiresEditGuard } from './pages/campaigns/campaign-wizard/new-campaign-permission.guard';
import { childFormRoutes } from './pages/campaigns/campaign-wizard/campaign-route.util';
import { childFormLeaveGuard } from './pages/campaigns/campaign-wizard/child-form-leave.guard';

/**
 * Public routes — accessible with OR without a /:clientId prefix.
 * These are auth/guest pages that do not require the clientId in the route tree.
 */
const publicRoutes: Routes = [
  {
    path: 'login',
    title: 'Login',
    loadComponent: () => import('./auth/login/login.component').then(m => m.LoginComponent),
    canActivate: [loginGuard]
  },
  {
    path: 'auth/login',
    title: 'Login',
    loadComponent: () => import('./auth/login/login.component').then(m => m.LoginComponent),
    canActivate: [loginGuard]
  },
  {
    path: 'auth/forgot-password',
    title: 'Forgot Password',
    loadComponent: () => import('./auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent),
    canActivate: [guestGuard]
  },
  {
    path: 'forgot-password',
    title: 'Forgot Password',
    loadComponent: () => import('./auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent),
    canActivate: [guestGuard]
  },
  {
    path: 'auth/reset-password',
    title: 'Reset Password',
    loadComponent: () => import('./auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
    resolve: { resetToken: passwordResetTokenResolver },
    canActivate: [guestGuard]
  },
  {
    path: 'reset-password',
    title: 'Reset Password',
    loadComponent: () => import('./auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
    resolve: { resetToken: passwordResetTokenResolver },
    canActivate: [guestGuard]
  },
  {
    path: 'auth/reset-success',
    title: 'Password Reset',
    loadComponent: () => import('./auth/reset-success/reset-success.component').then(m => m.ResetSuccessComponent),
    canActivate: [guestGuard]
  },
  {
    path: 'reset-success',
    title: 'Password Reset',
    loadComponent: () => import('./auth/reset-success/reset-success.component').then(m => m.ResetSuccessComponent),
    canActivate: [guestGuard]
  },
  {
    path: 'auth/forgot-success',
    title: 'Check Your Email',
    loadComponent: () => import('./auth/forgot-success/forgot-success.component').then(m => m.ForgotSuccessComponent),
    canActivate: [guestGuard]
  },
  {
    path: 'forgot-success',
    title: 'Check Your Email',
    loadComponent: () => import('./auth/forgot-success/forgot-success.component').then(m => m.ForgotSuccessComponent),
    canActivate: [guestGuard]
  },
  {
    path: 'totp-setup',
    title: 'Set Up Authenticator',
    loadComponent: () => import('./auth/totp-setup/totp-setup.component').then(m => m.TotpSetupComponent),
    canActivate: [guestGuard]
  },
  {
    path: 'auth/verify-otp',
    title: 'Verify Code',
    loadComponent: () => import('./auth/verify-otp/verify-otp.component').then(m => m.VerifyOtpComponent),
    canActivate: [otpVerificationGuard]
  },
  {
    path: 'verify-otp',
    title: 'Verify Code',
    loadComponent: () => import('./auth/verify-otp/verify-otp.component').then(m => m.VerifyOtpComponent),
    canActivate: [otpVerificationGuard]
  },
  {
    path: 'register',
    title: 'Register',
    loadComponent: () => import('./auth/register/register.component').then(m => m.RegisterComponent),
    resolve: { invitationToken: invitationTokenResolver },
    canActivate: [guestGuard]
  },
  {
    path: 'invalid-invitation',
    title: 'Invalid Invitation',
    loadComponent: () => import('./auth/invalid-invitation/invalid-invitation.component').then(m => m.InvalidInvitationComponent),
    canActivate: [guestGuard]
  },
  {
    path: 'invalid-reset-link',
    title: 'Invalid Reset Link',
    loadComponent: () => import('./auth/invalid-reset-link/invalid-reset-link.component').then(m => m.InvalidResetLinkComponent),
    canActivate: [guestGuard]
  },
  {
    // Public landing page for the member email-change verification link (no auth: the member is not signed in).
    path: 'verify-email',
    title: 'Verify Email',
    loadComponent: () => import('./pages/verify-email/verify-email.component').then(m => m.VerifyEmailComponent)
  }
];

/**
 * Protected routes — ONLY reachable under /:clientId.
 * This guarantees that getCurrentClientId() always finds clientId in the route params
 * when any of these pages are active.
 */
const protectedRoutes: Routes = [
  {
    path: 'access-denied',
    title: 'Access Denied',
    loadComponent: () => import('./pages/access-denied/access-denied.component').then(m => m.AccessDeniedComponent),
    canActivate: [authGuard]
  },
  {
    path: '',
    loadComponent: () => import('./layout/master/master.component').then(m => m.MasterComponent),
    canActivate: [authGuard],
    resolve: { userInfo: userResolver },
    children: [
      {
        // Personal profile — intentionally not under /admin-settings, which is
        // gated on ADMIN_SETTINGS read. Every signed-in user reaches their own
        // profile, so authGuard on the parent route is the only gate.
        path: 'profile',
        title: 'My Profile',
        loadComponent: () => import('./pages/profile/profile.component').then(m => m.ProfileComponent)
      },
      {
        path: 'dashboard',
        title: 'Dashboard',
        loadComponent: () => import('./pages/dashboard/dashboard.component').then(m => m.DashboardComponent),
        canActivate: [permissionGuard(FeatureName.DASHBOARD, 'read')]
      },
      {
        path: 'admin-settings/overview',
        title: 'Admin Settings',
        loadComponent: () => import('./pages/overview/overview.component').then(m => m.OverviewComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read')]
      },
      {
        path: 'admin-settings/tenants',
        title: 'Tenants',
        loadComponent: () => import('./pages/tenants/tenants.component').then(m => m.TenantsComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard(FeatureName.TENANT, 'read')]
      },
      {
        path: 'admin-settings/stores-locations',
        title: 'Stores & Locations',
        loadComponent: () => import('./pages/stores-locations/stores-locations.component').then(m => m.StoresLocationsComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard(FeatureName.STORES_LOCATIONS, 'read')]
      },
      {
        path: 'admin-settings/stores-locations/add',
        title: 'Add Store',
        loadComponent: () => import('./pages/stores-locations/add-store-location/add-store-location.component').then(m => m.AddStoreLocationComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard(FeatureName.STORES_LOCATIONS, 'edit')]
      },
      {
        path: 'admin-settings/products',
        title: 'Products',
        loadComponent: () => import('./pages/products/products.component').then(m => m.ProductsComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard(FeatureName.PRODUCTS, 'read')]
      },
      {
        path: 'admin-settings/team-members',
        title: 'Team Members',
        loadComponent: () => import('./pages/team-members/team-members.component').then(m => m.TeamMembersComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard(FeatureName.TEAM_MEMBER, 'read')]
      },
      {
        path: 'admin-settings/team-members/invite/:id',
        title: 'Invite Team Member',
        loadComponent: () => import('./pages/team-members/add-team-member/add-team-member.component').then(m => m.AddTeamMemberComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard(FeatureName.TEAM_MEMBER, 'edit')]
      },
      {
        path: 'admin-settings/team-members/invite',
        title: 'Invite Team Member',
        loadComponent: () => import('./pages/team-members/add-team-member/add-team-member.component').then(m => m.AddTeamMemberComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard(FeatureName.TEAM_MEMBER, 'edit')]
      },
      {
        // The API Users list is now Admin Settings › API Key › API Access; old links land there.
        path: 'admin-settings/api-users',
        pathMatch: 'full',
        redirectTo: 'admin-settings/api-key/access'
      },
      {
        path: 'admin-settings/api-users/add',
        title: 'Add API User',
        loadComponent: () => import('./pages/api-users/add-api-user/add-api-user.component').then(m => m.AddApiUserComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard(FeatureName.API_USER, 'edit')]
      },
      {
        path: 'admin-settings/api-users/edit/:userId',
        title: 'Edit API User',
        loadComponent: () => import('./pages/api-users/add-api-user/add-api-user.component').then(m => m.AddApiUserComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard(FeatureName.API_USER, 'edit')],
        resolve: { apiUser: apiUserResolver }
      },
      {
        path: 'admin-settings/email-templates',
        title: 'Email Templates',
        loadComponent: () => import('./pages/email-templates/email-templates.component').then(m => m.EmailTemplatesComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard(FeatureName.EMAIL_TEMPLATES, 'read')]
      },
      {
        path: 'admin-settings/email-templates/add',
        title: 'Add Email Template',
        loadComponent: () => import('./pages/email-templates/add-email-template/add-email-template.component').then(m => m.AddEmailTemplateComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard(FeatureName.EMAIL_TEMPLATES, 'edit')]
      },
      {
        path: 'admin-settings/email-templates/edit/:id',
        title: 'Edit Email Template',
        loadComponent: () => import('./pages/email-templates/add-email-template/add-email-template.component').then(m => m.AddEmailTemplateComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard(FeatureName.EMAIL_TEMPLATES, 'edit')]
      },
      {
        path: 'admin-settings/roles-permissions',
        title: 'Roles & Permissions',
        loadComponent: () => import('./pages/roles-permissions/roles-permissions.component').then(m => m.RolesPermissionsComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard(FeatureName.ROLES_PERMISSION, 'read')]
      },
      {
        // Actions performed by admins (dbo.Audit). Gated by Roles & Permissions, matching the API's AdminAudit alias.
        path: 'admin-settings/admin-audit',
        title: 'Audit',
        loadComponent: () => import('./pages/admin-audit/admin-audit.component').then(m => m.AdminAuditComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard(FeatureName.ROLES_PERMISSION, 'read')]
      },
      {
        path: 'admin-settings/roles-permissions/create',
        title: 'Create Role',
        loadComponent: () => import('./pages/roles-permissions/create-role/create-role.component').then(m => m.CreateRoleComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard(FeatureName.ROLES_PERMISSION, 'edit')]
      },
      {
        path: 'admin-settings/client-configuration',
        title: 'Client Configuration',
        loadComponent: () => import('./pages/client-configuration/client-configuration.component').then(m => m.ClientConfigurationComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard(FeatureName.CLIENT_CONFIGURATION, 'read')]
      },
      {
        path: 'admin-settings/site-configuration',
        title: 'Campaign Configuration',
        loadComponent: () => import('./pages/site-configuration/site-configuration.component').then(m => m.SiteConfigurationComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard(FeatureName.SITE_CONFIGURATION, 'read')]
      },
      {
        path: 'admin-settings/portal-configuration',
        title: 'Portal Configuration',
        loadComponent: () => import('./pages/portal-configuration/portal-configuration.component').then(m => m.PortalConfigurationComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard(FeatureName.PORTAL_CONFIGURATION, 'read')]
      },
      {
        path: 'admin-settings/widget-configuration',
        title: 'Widget Configuration',
        loadComponent: () => import('./pages/widget-configuration/widget-configuration.component').then(m => m.WidgetConfigurationComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard(FeatureName.WIDGET_CONFIGURATION, 'read')]
      },
      {
        // Tabs: providers (default), integrations, access (API Users + inbound credentials).
        // Reachable with either the API Key or the API Users permission; each tab checks its own.
        path: 'admin-settings/api-key',
        title: 'API Key',
        loadComponent: () => import('./pages/api-key/api-key.component').then(m => m.ApiKeyComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard([FeatureName.API_KEY, FeatureName.API_USER], 'read')]
      },
      {
        path: 'admin-settings/api-key/:tab',
        title: 'API Key',
        loadComponent: () => import('./pages/api-key/api-key.component').then(m => m.ApiKeyComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard([FeatureName.API_KEY, FeatureName.API_USER], 'read')]
      },
      {
        path: 'admin-settings/recurring-jobs',
        title: 'Recurring Jobs',
        loadComponent: () => import('./pages/recurring-jobs/recurring-jobs.component').then(m => m.RecurringJobsComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read'), permissionGuard(FeatureName.RECURRING_JOBS, 'read')]
      },
      {
        // No dedicated feature permission yet — Admin Settings read is the gate.
        path: 'admin-settings/expiration-rules',
        title: 'Expiration Rules',
        loadComponent: () => import('./pages/expiration-rules/expiration-rules.component').then(m => m.ExpirationRulesComponent),
        canActivate: [permissionGuard(FeatureName.ADMIN_SETTINGS, 'read')]
      },
      {
        path: 'campaigns',
        title: 'Campaigns',
        loadComponent: () => import('./pages/campaigns/campaigns.component').then(m => m.CampaignsComponent),
        canActivate: [permissionGuard(FeatureName.CAMPAIGNS, 'read')]
      },
      // Legacy create routes now redirect into the wizard shell (Setup step).
      {
        path: 'campaigns/create-campaign',
        redirectTo: 'campaigns/new/setup',
        pathMatch: 'full'
      },
      {
        path: 'campaigns/create-campaign/:campaignId',
        redirectTo: 'campaigns/:campaignId/setup'
      },
      // Legacy review routes now redirect into the wizard shell (Review step).
      {
        path: 'campaigns/review/:campaignId',
        redirectTo: 'campaigns/:campaignId/review'
      },
      {
        path: 'campaigns/review',
        redirectTo: 'campaigns/new/review',
        pathMatch: 'full'
      },
      // Legacy step routes now redirect into the wizard shell.
      {
        path: 'campaigns/tiers/:campaignId',
        redirectTo: 'campaigns/:campaignId/tiers'
      },
      {
        path: 'campaigns/tiers',
        redirectTo: 'campaigns/new/tiers',
        pathMatch: 'full'
      },
      {
        path: 'campaigns/segments/:campaignId',
        title: 'Campaign Segments',
        loadComponent: () => import('./pages/campaigns/segments/segments.component').then(m => m.SegmentsComponent),
        canActivate: [permissionGuard(FeatureName.CAMPAIGNS, 'edit')]
      },
      {
        path: 'campaigns/segments',
        redirectTo: 'campaigns/new/segments',
        pathMatch: 'full'
      },
      {
        path: 'campaigns/promotions',
        redirectTo: 'campaigns/new/promotions',
        pathMatch: 'full'
      },
      {
        path: 'campaigns/coupons',
        redirectTo: 'campaigns/new/coupons',
        pathMatch: 'full'
      },
      {
        path: 'campaigns/portal',
        redirectTo: 'campaigns/new/portal',
        pathMatch: 'full'
      },
      // --- Campaign wizard shell ---
      //
      // ONE route for both creating and editing: `new` is a value of `:campaignId`, not a route
      // of its own. It used to be its own block, and that split was the cause of a family of bugs.
      // The first save mints the campaign and the shell rewrites the address bar off `/new` — and
      // because the two blocks were separate `Route` objects, Angular's default reuse strategy saw
      // a different route and destroyed the whole wizard to render the same screen again. Mid-save.
      // That cancelled the section writes still queued behind the mint, tore down side panels while
      // their save was in flight, and fired `canDeactivate` — so the wizard asked the user whether
      // to discard their own work, for a navigation they never requested. Sharing one config makes
      // the id swap a parameter change: same component, no teardown, no guard, no prompt.
      //
      // Must stay AFTER the static `campaigns/*` routes above so they match first.
      {
        path: 'campaigns/:campaignId',
        loadComponent: () => import('./pages/campaigns/campaign-wizard-layout/campaign-wizard-layout.component').then(m => m.CampaignWizardLayoutComponent),
        // Warn before the wizard is destroyed with unsaved edits still in it (ADR 0001).
        canDeactivate: [unsavedChangesGuard],
        // Shell allows 'read' so "view details" (Review) works for read-only
        // users; editable steps below add their own 'edit' guard. Creating a campaign is an
        // edit whichever step it starts on, which is what the second guard restores now that
        // `/campaigns/new` is served from here rather than from its own 'edit'-guarded route.
        canActivate: [permissionGuard(FeatureName.CAMPAIGNS, 'read'), newCampaignRequiresEditGuard],
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'setup' },
          {
            path: 'setup',
            title: 'Campaign Setup',
            data: { stepId: 'setup' },
            // Each step opens on read (view-only without edit); a new campaign already needs edit (shell guard).
            canActivate: [permissionGuard(FeatureName.CAMPAIGNS, 'read')],
            loadComponent: () => import('./pages/campaigns/create-campaign/create-campaign.component').then(m => m.CreateCampaignComponent)
          },
          {
            path: 'tiers',
            title: 'Campaign Tiers',
            data: { stepId: 'tiers' },
            canActivate: [permissionGuard(FeatureName.CAMPAIGN_TIERS, 'read')],
            loadComponent: () => import('./pages/campaigns/tier-program-setup/tier-program-setup.component').then(m => m.TierProgramSetupComponent)
          },
          {
            path: 'segments',
            title: 'Campaign Segments',
            data: { stepId: 'segments' },
            canActivate: [permissionGuard(FeatureName.CAMPAIGN_SEGMENTS, 'read')],
            loadComponent: () => import('./pages/campaigns/segments/segments.component').then(m => m.SegmentsComponent)
          },
          {
            path: 'catalogs',
            title: 'Campaign Catalogs',
            data: { stepId: 'catalogs' },
            canActivate: [permissionGuard(FeatureName.CAMPAIGN_CATALOGS, 'read')],
            loadComponent: () => import('./pages/campaigns/catalogs/catalogs.component').then(m => m.CatalogsComponent),
            // `catalogs/new`, `catalogs/edit/:rowId` — see childFormRoutes.
            children: childFormRoutes('catalogs', [childFormLeaveGuard])
          },
          {
            path: 'coupons',
            title: 'Campaign Coupons',
            data: { stepId: 'coupons' },
            canActivate: [permissionGuard(FeatureName.CAMPAIGN_COUPONS, 'read')],
            loadComponent: () => import('./pages/campaigns/coupons/coupons.component').then(m => m.CouponsComponent),
            children: childFormRoutes('coupons', [childFormLeaveGuard])
          },
          {
            path: 'promo-codes',
            title: 'Campaign Promo Codes',
            data: { stepId: 'promo-codes' },
            canActivate: [permissionGuard(FeatureName.CAMPAIGN_PROMO_CODES, 'read')],
            loadComponent: () => import('./pages/campaigns/promo-codes/promo-codes.component').then(m => m.PromoCodesComponent)
          },
          {
            path: 'promotions',
            title: 'Campaign Promotions',
            data: { stepId: 'promotions' },
            canActivate: [permissionGuard(FeatureName.CAMPAIGN_PROMOTIONS, 'read')],
            loadComponent: () => import('./pages/campaigns/promotions/promotions.component').then(m => m.PromotionsComponent),
            // Also `…/edit/:rowId/source/new` etc. — a goal's source promotion, opened over it.
            children: childFormRoutes('promotions', [childFormLeaveGuard], { withSource: true })
          },
          {
            path: 'product-collections',
            title: 'Product Collections',
            data: { stepId: 'product-collections' },
            canActivate: [permissionGuard(FeatureName.CAMPAIGN_PRODUCT_COLLECTIONS, 'read')],
            loadComponent: () => import('./pages/campaigns/campaign-wizard/placeholder-step/wizard-placeholder-step.component').then(m => m.WizardPlaceholderStepComponent)
          },
          {
            path: 'portal',
            title: 'Frontend Portal',
            data: { stepId: 'portal' },
            canActivate: [permissionGuard(FeatureName.CAMPAIGN_FRONTEND_PORTAL, 'read')],
            loadComponent: () => import('./pages/campaigns/portal-step/portal-step.component').then(m => m.PortalStepComponent)
          },
          {
            path: 'review',
            title: 'Campaign Review',
            data: { stepId: 'review' },
            loadComponent: () => import('./pages/campaigns/review-campaign/review-campaign.component').then(m => m.ReviewCampaignComponent)
          }
        ]
      },
      {
        path: 'members',
        title: 'Members',
        loadComponent: () => import('./pages/members/members.component').then(m => m.MembersComponent),
        canActivate: [permissionGuard(FeatureName.MEMBERS, 'read')]
      },
      {
        path: 'members/search',
        title: 'Member Search',
        loadComponent: () => import('./pages/member-search/member-search.component').then(m => m.MemberSearchComponent)
      },
      {
        path: 'members/transaction-search',
        redirectTo: 'members/search'
      },
      {
        path: 'members/:id',
        title: 'Member Details',
        loadComponent: () => import('./pages/member-layout/member-layout.component').then(m => m.MemberLayoutComponent),
        canActivate: [permissionGuard(FeatureName.MEMBERS, 'read')],
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./pages/member-layout/member-default-redirect.component').then(m => m.MemberDefaultRedirectComponent)
          },
          {
            path: 'dashboard',
            title: 'Member Dashboard',
            loadComponent: () => import('./pages/member-dashboard/member-dashboard.component').then(m => m.MemberDashboardComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_DASHBOARD, 'read')]
          },
          {
            path: 'activities',
            title: 'Member Activities',
            loadComponent: () =>
              import('./pages/member-activities/member-activities.component').then(m => m.MemberActivitiesComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_DASHBOARD, 'read')]
          },
          {
            path: 'promotions',
            title: 'Member Promotions',
            loadComponent: () =>
              import('./pages/member-promotions/member-promotions.component').then(m => m.MemberPromotionsComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_DASHBOARD, 'read')]
          },
          {
            path: 'coupons-vouchers',
            title: 'Coupons & Vouchers',
            loadComponent: () =>
              import('./pages/member-coupons-vouchers/member-coupons-vouchers.component').then(m => m.MemberCouponsVouchersComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_DASHBOARD, 'read')]
          },
          {
            path: 'promotion-codes',
            title: 'Promotion Codes',
            loadComponent: () =>
              import('./pages/member-promotion-codes/member-promotion-codes.component').then(m => m.MemberPromotionCodesComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_DASHBOARD, 'read')]
          },
          {
            path: 'personal-info',
            title: 'Personal Info',
            loadComponent: () => import('./pages/member-personal-info/member-personal-info.component').then(m => m.MemberPersonalInfoComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_PERSONAL_INFO, 'read')]
          },
          {
            path: 'extra-info',
            title: 'Extra Info',
            loadComponent: () => import('./pages/member-tabs/extra-info/member-extra-info.component').then(m => m.MemberExtraInfoComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_EXTRA_INFO, 'read'), partialViewActiveGuard('MemberDetails', 'Extra Info')]
          },
          {
            path: 'address',
            title: 'Address',
            loadComponent: () => import('./pages/member-tabs/address/member-address.component').then(m => m.MemberAddressComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_ADDRESS, 'read'), partialViewActiveGuard('MemberDetails', 'Address')]
          },
          {
            path: 'manual-claim',
            title: 'Manual Points',
            loadComponent: () => import('./pages/member-tabs/manual-points/manual-points.component').then(m => m.ManualPointsComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_MANUAL_POINTS, 'read'), partialViewActiveGuard('MemberDetails', 'Manual Claim')]
          },
          {
            path: 'audit',
            title: 'Audit',
            loadComponent: () => import('./pages/member-audit-logs/member-audit-logs.component').then(m => m.MemberAuditLogsComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_MEMBER_AUDIT, 'read')]
          },
          {
            path: 'devices',
            title: 'Loyalty Cards',
            loadComponent: () => import('./pages/member-tabs/devices/member-devices.component').then(m => m.MemberDevicesComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_DEVICES, 'read')]
          },
          {
            path: 'segment',
            title: 'Segments & Tiers',
            loadComponent: () => import('./pages/member-tabs/segments-tiers/member-segments-tiers.component').then(m => m.MemberSegmentsTiersComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_SEGMENT, 'read')]
          },
          {
            path: 'member-link/manual-search',
            title: 'Member Link Search',
            loadComponent: () =>
              import('./pages/member-link/manual-member-search/manual-member-search.component').then(m => m.ManualMemberSearchComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_LINK, 'read')]
          },
          {
            path: 'member-link/review-merge/:secondaryMemberId',
            title: 'Review Merge',
            loadComponent: () => import('./pages/member-link/review-merge/review-merge.component').then(m => m.ReviewMergeComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_LINK, 'read')]
          },
          {
            path: 'member-link',
            title: 'Member Link',
            loadComponent: () => import('./pages/member-link/member-link.component').then(m => m.MemberLinkComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_LINK, 'read')]
          },
          {
            path: 'receipts-submission',
            title: 'Receipt Submissions',
            loadComponent: () => import('./pages/member-tabs/receipt-submissions/receipt-submissions.component').then(m => m.ReceiptSubmissionsComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_RECEIPTS, 'read')]
          },
          {
            path: 'order-rewards',
            title: 'Order Rewards',
            loadComponent: () =>
              import('./pages/member-order-rewards/member-order-rewards.component').then(m => m.MemberOrderRewardsComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_ORDER_REWARDS, 'read')]
          },
          {
            path: 'transaction-info',
            title: 'Transaction Info',
            loadComponent: () =>
              import('./pages/member-transaction-info/member-transaction-info.component').then(m => m.MemberTransactionInfoComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_TRANSACTION_INFO, 'read'), partialViewActiveGuard('MemberDetails', 'TransactionInfo')]
          },
          {
            path: 'loyalty',
            title: 'Loyalty',
            loadComponent: () => import('./pages/member-dashboard/member-dashboard.component').then(m => m.MemberDashboardComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_LOYALTY, 'read')]
          },
          {
            path: 'incentives',
            title: 'Member Incentives',
            loadComponent: () => import('./pages/member-incentives/member-incentives.component').then(m => m.MemberIncentivesComponent),
            canActivate: [permissionGuard(FeatureName.INCENTIVES, 'read')]
          },
          {
            path: 'stamp-cards',
            title: 'Punch Cards',
            loadComponent: () => import('./pages/member-stamp-cards/member-stamp-cards.component').then(m => m.MemberStampCardsComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_STAMP_CARDS, 'read')]
          },
          {
            path: 'account-info',
            title: 'Account Info',
            loadComponent: () => import('./pages/member-tabs/account-info/account-info.component').then(m => m.MemberAccountInfoComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_ACCOUNT_INFO, 'read'), partialViewActiveGuard('MemberDetails', 'Account Info')]
          },
          {
            path: 'redeem-vouchers',
            title: 'Redeem Vouchers',
            loadComponent: () => import('./pages/member-tabs/redeem-vouchers/redeem-vouchers.component').then(m => m.MemberRedeemVouchersComponent),
            canActivate: [permissionGuard(FeatureName.MEMBER_REDEEM_VOUCHERS, 'read'), partialViewActiveGuard('MemberDetails', 'Redeem Vouchers')]
          }
        ]
      },
      {
        path: 'incentives',
        title: 'Incentives',
        loadComponent: () => import('./pages/incentives/incentives.component').then(m => m.IncentivesComponent),
        canActivate: [permissionGuard(FeatureName.INCENTIVES, 'read')]
      },
      {
        path: 'incentives/rewards',
        title: 'Rewards',
        loadComponent: () => import('./pages/incentives/rewards/rewards.component').then(m => m.RewardsComponent),
        canActivate: [permissionGuard(FeatureName.INCENTIVES, 'read'), permissionGuard(FeatureName.REWARDS, 'read')]
      },
      {
        path: 'incentives/prizes',
        title: 'Prizes',
        loadComponent: () => import('./pages/incentives/prizes/prizes.component').then(m => m.PrizesComponent),
        canActivate: [permissionGuard(FeatureName.INCENTIVES, 'read'), permissionGuard(FeatureName.PRIZES, 'read')]
      },
      {
        path: 'segmentation',
        title: 'Segmentation',
        loadComponent: () => import('./pages/segmentation/segmentation.component').then(m => m.SegmentationComponent),
        canActivate: [permissionGuard(FeatureName.SEGMENTATION, 'read')]
      },
      {
        path: 'analytics',
        title: 'Snipp Analytics',
        loadComponent: () => import('./pages/reports/reports.component').then(m => m.ReportsComponent),
        canActivate: [permissionGuard(FeatureName.SNIPP_ANALYTICS, 'read')]
      }
    ]
  }
];

export const routes: Routes = [
  // clientId is mandatory — all routes live under /:clientId
  {
    path: ':clientId',
    canMatch: [clientIdGuard],
    children: [
      // /:clientId → /:clientId/login
      { path: '', redirectTo: 'login', pathMatch: 'full' },
      ...publicRoutes,
      ...protectedRoutes,
      // Unknown route within a client context — clientId is available for "Go Home"
      { path: '**', title: 'Page Not Found', loadComponent: () => import('./pages/not-found/not-found.component').then(m => m.NotFoundComponent) }
    ]
  },
  // Bare URL with no clientId (e.g. /) — show 404 without navigation
  { path: '**', title: 'Page Not Found', loadComponent: () => import('./pages/not-found/not-found.component').then(m => m.NotFoundComponent) }
];
